import { useState } from "react";
import { Link } from "react-router-dom";
import EpisodeList from "./EpisodeList";
import MediaCard from "@/components/feature/MediaCard";
import SectionHeading from "@/components/base/SectionHeading";
import type { MediaItem } from "@/types/media";
import { formatViews, getRelated } from "@/utils/mediaData";

interface DetailViewProps {
  item?: MediaItem;
  categoryLabel: string;
  categoryTo: string;
  breadcrumb: string;
}

export default function DetailView({ item, categoryLabel, categoryTo, breadcrumb }: DetailViewProps) {
  const [modalOpen, setModalOpen] = useState(false);

  if (!item) {
    return (
      <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center justify-center text-center">
        <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
        </span>
        <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-2">影视资源不存在或已下架</h1>
        <p className="text-sm text-foreground-500 mb-6">你访问的内容可能已被移除，或链接地址有误。</p>
        <div className="flex flex-wrap items-center gap-3 justify-center">
          <Link
            to={categoryTo}
            className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            返回{categoryLabel}
          </Link>
          <Link
            to="/"
            className="h-10 px-5 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-semibold whitespace-nowrap hover:border-primary-300 transition-colors"
          >
            回到首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item, 6);

  return (
    <div className="w-full">
      <section className="relative w-full bg-foreground-950 overflow-hidden">
        <img
          src={item.heroImage ?? item.poster}
          alt={`${item.title}剧照背景`}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-top opacity-30 blur-sm scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/85 to-foreground-950/60"></div>

        <div className="relative z-10 w-full px-4 md:px-8 pt-8 md:pt-12 pb-8 md:pb-10">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-white/60 mb-5">
            <Link to="/" className="hover:text-white transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={categoryTo} className="hover:text-white transition-colors">
              {breadcrumb}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">{item.title}</span>
          </nav>

          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            <div className="shrink-0 w-[150px] sm:w-[180px] md:w-[230px] mx-auto md:mx-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-800">
                <img
                  src={item.poster}
                  alt={`${item.title}海报`}
                  title={`${item.title} - 91n影视大全`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0 text-center md:text-left">
              <div className="flex flex-wrap items-center gap-2 justify-center md:justify-start mb-2">
                <span className="h-6 px-2 rounded-md bg-primary-500 text-background-50 text-xs font-semibold flex items-center">
                  {item.type === "movie" ? "电影" : "电视剧"}
                </span>
                {item.updatedTo && (
                  <span className="h-6 px-2 rounded-md bg-accent-500 text-background-50 text-xs font-semibold flex items-center">
                    {item.updatedTo}
                  </span>
                )}
              </div>
              <h1 className="font-heading font-black text-white text-2xl md:text-4xl mb-3">{item.title}</h1>

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 justify-center md:justify-start text-sm text-white/80 mb-4">
                <span className="flex items-center gap-1 text-secondary-300 font-bold text-base">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
                <span>{item.year}</span>
                <span>{item.region}</span>
                {item.type === "movie" ? <span>{item.duration}</span> : <span>共 {item.episodeCount} 集</span>}
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(item.views)} 次浏览
                </span>
              </div>

              <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
                {item.genres.map((g) => (
                  <span key={g} className="h-7 px-3 rounded-md bg-white/12 border border-white/15 text-white/85 text-xs flex items-center">
                    {g}
                  </span>
                ))}
              </div>

              <div className="text-sm text-white/80 space-y-1.5 text-left max-w-2xl mx-auto md:mx-0">
                <p>
                  <span className="text-white/50">导演：</span>
                  {item.director}
                </p>
                <p>
                  <span className="text-white/50">主演：</span>
                  {item.cast.join(" / ")}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 justify-center md:justify-start mt-6">
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors"
                >
                  <i className="ri-play-fill text-lg"></i>
                  在线播放演示入口
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  className="h-11 px-6 rounded-md bg-white/12 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors"
                >
                  <i className="ri-download-2-line text-lg"></i>
                  下载演示
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <section className="mb-10">
          <SectionHeading title="剧情简介" icon="ri-file-text-line" />
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed max-w-4xl">{item.synopsis}</p>
        </section>

        {item.type === "tv" && item.episodeCount && (
          <section className="mb-10">
            <SectionHeading
              title="剧集目录"
              subtitle={`共 ${item.episodeCount} 集 · ${item.updatedTo ?? ""}`}
              icon="ri-list-ordered"
            />
            <EpisodeList total={item.episodeCount} onSelect={() => setModalOpen(true)} />
          </section>
        )}

        <section>
          <SectionHeading title="相关推荐" subtitle="根据类型与标签为你推荐" icon="ri-magic-line" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {related.map((rel) => (
              <MediaCard key={rel.id} item={rel} />
            ))}
          </div>
        </section>

        <div className="mt-10 pt-6 border-t border-background-200">
          <Link
            to={categoryTo}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors"
          >
            <i className="ri-links-line"></i>
            查看全部{categoryLabel}内容
          </Link>
        </div>
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 animate-fade-in"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-background-50 p-6 md:p-7 text-center animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="mx-auto w-14 h-14 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center mb-4">
              <i className="ri-movie-line text-2xl"></i>
            </span>
            <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">播放功能演示提示</h3>
            <p className="text-sm text-foreground-600 leading-relaxed mb-6">
              本演示仅展示页面结构，播放功能待后端接入。当前站点不提供真实视频播放与下载服务。
            </p>
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="w-full h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </div>
  );
}